import { Stack } from '@mui/material';
import { ReactNode, useLayoutEffect, useRef } from 'react';

import { FOOTER_HEIGHT } from '@/components/navigation/Footer';

import { useHouseholdChatReplacement } from '../hooks/useHouseholdChatReplacement';
import { HouseholdChatViewedContext } from '../hooks/useHouseholdChatViewedContext';
import { FullScreenModalHouseholdChatEntryPoint } from './FullScreenModalHouseholdChatEntryPoint';
import { HouseholdChat } from './HouseholdChat';

interface FullScreenModalHouseholdChatHostRenderProps {
  householdChatPane?: ReactNode;
  isHouseholdChatReplacementEnabled: boolean;
}

interface FullScreenModalHouseholdChatHostProps {
  children: (props: FullScreenModalHouseholdChatHostRenderProps) => ReactNode;
  viewedContext?: HouseholdChatViewedContext | null;
  responsiveInModal?: boolean;
  modalHeaderHeight?: number;
  entryPointBottomOffset?: number;
}

/**
 * Moves the household chat from the nearest provider into a full-screen modal
 * while it is mounted. Keeping the provider above this host preserves the
 * active conversation when the modal host mounts or unmounts.
 */
export function FullScreenModalHouseholdChatHost({
  children,
  viewedContext,
  responsiveInModal,
  modalHeaderHeight,
  entryPointBottomOffset = FOOTER_HEIGHT + 16,
}: FullScreenModalHouseholdChatHostProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { householdChatContext, isHouseholdChatReplacementEnabled } =
    useHouseholdChatReplacement();
  const setModalViewedContext = householdChatContext?.setModalViewedContext;
  useLayoutEffect(() => {
    if (!isHouseholdChatReplacementEnabled || viewedContext === undefined)
      return;
    setModalViewedContext?.(viewedContext);
    return () => setModalViewedContext?.(undefined);
  }, [isHouseholdChatReplacementEnabled, setModalViewedContext, viewedContext]);

  const registerFullScreenModalHost =
    householdChatContext?.registerFullScreenModalHost;
  const unregisterFullScreenModalHost =
    householdChatContext?.unregisterFullScreenModalHost;
  const openChat = householdChatContext?.openChat;

  useLayoutEffect(() => {
    if (
      !isHouseholdChatReplacementEnabled ||
      !registerFullScreenModalHost ||
      !unregisterFullScreenModalHost
    ) {
      return;
    }

    registerFullScreenModalHost();
    return unregisterFullScreenModalHost;
  }, [
    isHouseholdChatReplacementEnabled,
    registerFullScreenModalHost,
    unregisterFullScreenModalHost,
  ]);

  const householdChatPane = isHouseholdChatReplacementEnabled ? (
    <HouseholdChat
      containerRef={containerRef}
      host="fullScreenModal"
      responsiveInModal={responsiveInModal}
      modalHeaderHeight={modalHeaderHeight}
    />
  ) : undefined;

  return (
    <Stack ref={containerRef} height="100%" minHeight={0} position="relative">
      {children({
        householdChatPane,
        isHouseholdChatReplacementEnabled,
      })}
      {isHouseholdChatReplacementEnabled &&
        !householdChatContext?.isChatOpen &&
        openChat && (
          <FullScreenModalHouseholdChatEntryPoint
            bottomOffset={entryPointBottomOffset}
            onClick={openChat}
          />
        )}
    </Stack>
  );
}
